import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Typography } from 'antd';
import { DeleteOutlined, ShareAltOutlined, CloseOutlined, BugOutlined } from '@ant-design/icons';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import consoleLogger from '../utils/consoleLogger';

const { Text } = Typography;

const DebugLogger = () => {
  const [logs, setLogs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const logContainerRef = useRef(null);

  useEffect(() => {
    // Abonniere den globalen Logger
    const unsubscribe = consoleLogger.subscribe((newLogs) => {
      setLogs([...newLogs]);
    });

    // Cleanup beim Unmount
    return unsubscribe;
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logContainerRef.current && isOpen) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, isOpen]);

  const clearLogs = () => {
    consoleLogger.clearLogs();
  };

  const exportLogs = async () => {
    if (logs.length === 0) {
      alert('Keine Logs zum Exportieren vorhanden');
      return;
    }

    // Format logs as text
    const logText = logs.map(log => {
      return `[${log.timestamp}] ${log.type.toUpperCase()}\n${log.message}\n`;
    }).join('\n---\n\n');

    const fullText = `Debug Logs Export\nErstellt: ${new Date().toLocaleString('de-DE')}\nAnzahl Logs: ${logs.length}\n\n${'='.repeat(50)}\n\n${logText}`;

    try {
      if (Capacitor.isNativePlatform()) {
        // On mobile: Save to file and share
        const { Filesystem, Directory } = await import('@capacitor/filesystem');

        // Create filename with timestamp
        const filename = `debug-logs-${Date.now()}.txt`;

        // Write file to cache directory
        await Filesystem.writeFile({
          path: filename,
          data: fullText,
          directory: Directory.Cache,
          encoding: 'utf8'
        });

        // Get file URI
        const fileUri = await Filesystem.getUri({
          path: filename,
          directory: Directory.Cache
        });

        console.log('Log file created:', fileUri.uri);

        // Share the file
        await Share.share({
          title: 'Debug Logs',
          text: `Debug Logs (${logs.length} Einträge)`,
          url: fileUri.uri,
          dialogTitle: 'Debug Logs teilen'
        });
      } else {
        // Copy to clipboard on web
        await navigator.clipboard.writeText(fullText);
        alert('Logs in Zwischenablage kopiert!');
      }
    } catch (error) {
      console.error('Failed to share logs:', error);
      // Fallback: create a downloadable file
      const blob = new Blob([fullText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-logs-${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'error':
        return '#ff5252';
      case 'warn':
        return '#ffa726';
      default:
        return '#90caf9';
    }
  };

  return (
    <>
      {/* Open Debug Console Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          type="primary"
          icon={<BugOutlined />}
          block
        >
          Debug Console öffnen
        </Button>
      )}

      {/* Debug Log Panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50vh',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1e1e1e',
            color: '#fff',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: '1px solid #333',
              backgroundColor: '#2d2d2d',
            }}
          >
            <Text strong style={{ color: '#fff', fontSize: '14px' }}>
              Debug Console ({logs.length} logs)
            </Text>
            <Space size="small">
              <Button
                size="small"
                onClick={exportLogs}
                icon={<ShareAltOutlined />}
                title="Share/Export logs"
                style={{ color: '#fff' }}
              />
              <Button
                size="small"
                onClick={clearLogs}
                icon={<DeleteOutlined />}
                title="Clear logs"
                style={{ color: '#fff' }}
              />
              <Button
                size="small"
                onClick={() => setIsOpen(false)}
                icon={<CloseOutlined />}
                title="Close"
                style={{ color: '#fff' }}
              />
            </Space>
          </div>

          {/* Log Container */}
          <div
            ref={logContainerRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px',
              fontFamily: 'monospace',
              fontSize: '12px',
            }}
          >
            {logs.length === 0 ? (
              <Text style={{ color: '#888', fontSize: '12px' }}>
                Noch keine Logs...
              </Text>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '4px',
                    padding: '4px 8px',
                    borderLeft: `3px solid ${getLogColor(log.type)}`,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '2px',
                  }}
                >
                  <Text
                    style={{
                      color: '#888',
                      display: 'block',
                      marginBottom: '2px',
                      fontSize: '11px',
                    }}
                  >
                    [{log.timestamp}] {log.type.toUpperCase()}
                  </Text>
                  <pre
                    style={{
                      color: getLogColor(log.type),
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                      fontSize: '12px',
                      fontFamily: 'monospace',
                    }}
                  >
                    {log.message}
                  </pre>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DebugLogger;
